<template>
  <div>
    <div data-cy="bnpl-payment-schedule-modal">
      <VeeForm
        ref="form"
        class="c-form"
        :class="$style.form"
        :validation-schema="validationSchema"
        :initial-values="{
          amount: Number(amount) || null,
          payOutCurrency: selectedCurrency,
          dueDate: selectedDueDate,
        }"
        @submit="updateAndContinue"
      >
        <div :class="$style['amount-wrapper']">
          <InputCurrencyGroup
            name="amount"
            :required="true"
            :label="
              isInvoiceFlow
                ? $t('account.pages.pageVendors.bnplPaymentSchedule.amount', {
                    type: handleType,
                  })
                : $t(
                    'account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceAmount',
                  )
            "
            :currency-options="isInvoiceFlow ? currencyOptions : []"
            @change="currencyChange"
            :tooltip="
              isInvoiceFlow
                ? $t(
                    'account.pages.pageVendors.bnplPaymentSchedule.currencyToolTip',
                  )
                : ''
            "
            @input="updateAmountDebounced"
          />
          <InputDate
            v-if="isCashAdvanceFlow"
            name="dueDate"
            :label="
              $t(
                'account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceDueDate',
              )
            "
            :hint="formattedDueDateHint"
            :min-date="getDate(1)"
            :max-date="
              businessCapacityApproved
                ? new Date(businessCapacity.expiresAt)
                : getDate(30)
            "
            :manual-input="false"
            :date-format="'yy-mm-dd'"
            @date-select="updateDueDate"
          />
        </div>
        <div class="c-form__row">
          <div v-if="isLoading" :class="$style['grey-container']">
            <icon name="spinner" scale="2" />
            <p>
              {{ $t('account.pages.pageVendors.bnplPaymentSchedule.loading') }}
            </p>
          </div>

          <div v-else>
            <BillCardsRadio
              v-if="amount && amount !== '0.00'"
              :choices="repaymentSchedule?.repaymentSchedulesByMonth"
              :position="`${termChosen}`"
              :capacity-response="businessCapacity"
              :bill-amount="amountCents"
              @getStepValue="getStepValue"
              @updateAmount="updateAmount"
              @disableNext="disableNext"
            />
            <div
              v-else
              data-cy="card-rate-zero-state"
              :class="$style['grey-container']"
            >
              <p>
                {{
                  $t(
                    'account.pages.pageVendors.bnplPaymentSchedule.enterAnAmount',
                    { type: handleType.toLowerCase() },
                  )
                }}
              </p>
            </div>
          </div>
        </div>

        <NavigationButtons
          :back-label="$t(`account.pages.pageVendors.back`)"
          :forward-label="$t(`account.pages.pageVendors.nextStep`)"
          :forward-disabled="disabled"
          forward-type="submit"
          @back-navigate="$emit('prev-tab')"
        />
      </VeeForm>

      <div :class="$style['bnpl-subject-to-approval']">
        <sup>*</sup>{{ footnote }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import BillCardsRadio from '@/components/PayAnInvoiceModal/BillCardsRadio.vue';
import { PAY_OUT_CURRENCY_OPTIONS, DEFAULT_CURRENCY } from '@/data/payments';
import { dollarsToCents, centsToDollars } from '@/utils/currency';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { Form as VeeForm } from 'vee-validate';
import { object, string, number } from 'yup';
import { BillType } from '@/@types/bill.types';
import InputCurrencyGroup from '@/components/InputCurrencyGroup.vue';
import NavigationButtons from '@/components/buttons/NavigationButtons.vue';
import InputDate from '@/components/InputDate.vue';

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
});

const DEFAULT_REPAYMENT_TERMS = 4;
const INACTIVE_REPAYMENT_TERMS = 1;

export default {
  emits: [
    'get-repayment-term',
    'repayment-details',
    'next-tab',
    'prev-tab',
    'amount',
    'pay-out-currency',
  ],
  components: {
    BillCardsRadio,
    VeeForm,
    InputCurrencyGroup,
    NavigationButtons,
    InputDate,
  },
  props: {
    amount: {
      type: String,
      required: true,
    },
    selectedTerm: {
      type: [Number, String],
      required: true,
    },
    payloadCurrency: {
      type: String,
      default: '',
    },
    payloadDueDate: {
      type: [Date, String],
      default: null,
    },
  },
  data() {
    return {
      moneyMask: {
        code: PAY_OUT_CURRENCY_OPTIONS.USD.code,
        thousands: ',',
        decimal: '.',
        prefix: PAY_OUT_CURRENCY_OPTIONS.USD.symbol_native,
        precision: 2,
        masked: false,
      },
      isLoading: true,
      hasCurrencyChange: false,
      userTriggeredAmountError: false,
      termChosen:
        this.amount && this.amount !== '0.00'
          ? `${this.selectedTerm}`
          : `${DEFAULT_REPAYMENT_TERMS}`,
      disabled: false,
      invoiceSchema: null,
      cashAdvanceSchema: null,
      timeoutReference: null,
      selectedDueDate: this.payloadDueDate || this.getDate(1),
    };
  },
  beforeMount() {
    const invoiceSchemaShape = {
      amount: number()
        .nullable()
        .min(
          centsToDollars(this.minInvoiceAmountCents),
          this.$t(
            'account.pages.pageVendors.bnplPaymentSchedule.invoiceMinAmountError',
            {
              minAmount: formatter.format(
                centsToDollars(this.minInvoiceAmountCents),
              ),
            },
          ),
        )
        .required(this.$t('common.required')),
      payOutCurrency: string().required(this.$t('common.required')),
    };

    const cashAdvanceSchemaShape = {
      amount: number()
        .nullable()
        .max(
          centsToDollars(this.maxCashAdvanceAmountCents),
          this.$t(
            'account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceMaxAmountError',
            {
              maxAmount: formatter.format(
                centsToDollars(this.maxCashAdvanceAmountCents),
              ),
            },
          ),
        )
        .min(
          centsToDollars(this.minCashAdvanceAmountCents),
          this.$t(
            'account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceMinAmountError',
            {
              minAmount: formatter.format(
                centsToDollars(this.minCashAdvanceAmountCents),
              ),
            },
          ),
        )
        .required(this.$t('common.required')),
      payOutCurrency: string().required(this.$t('common.required')),
      dueDate: object().nullable(),
    };

    const availableCapacityCents =
      this.businessCapacity.availableCents ||
      this.remainingSelfReportedCapacityCents;

    if (availableCapacityCents > 0) {
      const availableCapacityDollars = availableCapacityCents / 100;
      const formatMoney = this.$filters.currency(availableCapacityCents);

      if (availableCapacityCents < this.maxCashAdvanceAmountCents) {
        // should change the limit of CASH ADVANCE amount field and update the error message
        cashAdvanceSchemaShape.amount = cashAdvanceSchemaShape.amount.max(
          availableCapacityDollars,
          this.$t(
            'account.pages.pageVendors.bnplPaymentSchedule.cashAdvanceCapacityError',
            { remainingCapacity: formatMoney },
          ),
        );
      }
      // should change the limit of INVOICE amount field and update the error message
      invoiceSchemaShape.amount = invoiceSchemaShape.amount.max(
        availableCapacityDollars,
        this.$t('account.pages.pageVendors.bnplPaymentSchedule.capacityError', {
          type: this.handleType,
          money: formatMoney,
        }),
      );
    }

    this.invoiceSchema = object(invoiceSchemaShape);
    this.cashAdvanceSchema = object(cashAdvanceSchemaShape);
  },
  computed: {
    ...mapGetters([
      'repaymentSchedule',
      'business',
      'businessId',
      'businessCapacity',
      'businessCapacityApproved',
      'isCashAdvanceFlow',
      'isInvoiceFlow',
      'remainingSelfReportedCapacityCents',
      'billSchema',
    ]),
    handleType() {
      if (this.isInvoiceFlow) {
        return this.$t('account.pages.pageVendors.invoice');
      } else if (this.isCashAdvanceFlow) {
        return this.$t('account.pages.pageVendors.cashAdvance');
      } else {
        return '';
      }
    },
    validationSchema() {
      if (this.isInvoiceFlow) {
        return this.invoiceSchema;
      } else if (this.isCashAdvanceFlow) {
        return this.cashAdvanceSchema;
      }
    },
    footnote() {
      return `
        ${this.$t('paymentsPage.extensionPlanFootnote')}
        ${
          this.hasCurrencyChange
            ? this.$t('paymentsPage.exchangeRateLegal')
            : ''
        }
      `;
    },
    amountCents() {
      return dollarsToCents(this.amount, this.selectedCurrency);
    },
    selectedTermRecord() {
      return this.repaymentSchedule?.repaymentSchedulesByMonth[
        this.selectedTerm
      ];
    },
    selectedTermSchedule() {
      return this.selectedTermRecord.schedule;
    },
    selectedTermRate() {
      return this.selectedTermRecord.feeRate;
    },
    selectedTermFeeCents() {
      return this.selectedTermRecord.feeAmountCents;
    },
    selectedTermFeeVersionId() {
      return this.repaymentSchedule?.feeVersionId;
    },
    weeklyPayment() {
      return centsToDollars(this.selectedTermRecord.paymentPerTermCents);
    },
    numWeeklyPayments() {
      return this.selectedTermRecord.totalNumPayments;
    },
    selectedCurrency() {
      return this.payloadCurrency ? this.payloadCurrency : this.moneyMask.code;
    },
    currencyOptions() {
      const keys = Object.keys(PAY_OUT_CURRENCY_OPTIONS);
      return keys.map((code) => ({
        label: `${PAY_OUT_CURRENCY_OPTIONS[code].name} (${code})`,
        code,
      }));
    },
    minInvoiceAmountCents() {
      return this.billSchema[BillType.INVOICE].amountCents.min;
    },
    minCashAdvanceAmountCents() {
      return this.billSchema[BillType.CASH_ADVANCE].amountCents.min;
    },
    maxCashAdvanceAmountCents() {
      return this.billSchema[BillType.CASH_ADVANCE].amountCents.max;
    },
    formattedDueDateHint() {
      if (this.businessCapacityApproved) {
        const expiresAt = new Date(this.businessCapacity.expiresAt);
        const options = {
          month: 'short',
          day: 'numeric',
        };

        const formattedDate = expiresAt.toLocaleDateString('en-US', options);
        return this.$t(
          'account.pages.pageVendors.bnplPaymentSchedule.dueDateHintApproved',
          {
            expiresAt: formattedDate,
          },
        );
      } else {
        return this.$t(
          'account.pages.pageVendors.bnplPaymentSchedule.dueDateHintUnapproved',
        );
      }
    },
  },
  watch: {
    amount(newAmount) {
      if (
        newAmount !== '0.00' &&
        this.selectedTerm === INACTIVE_REPAYMENT_TERMS
      ) {
        this.termChosen = `${DEFAULT_REPAYMENT_TERMS}`;
        this.$emit('get-repayment-term', DEFAULT_REPAYMENT_TERMS);
      }

      if (newAmount === '0.00') {
        this.termChosen = `${INACTIVE_REPAYMENT_TERMS}`;
        this.$emit('get-repayment-term', INACTIVE_REPAYMENT_TERMS);
      }

      this.getFeesSchedule(this.selectedCurrency);
    },
  },
  unmounted() {
    clearInterval(this.timeout);
  },
  async mounted() {
    await this.getFeesSchedule(this.selectedCurrency);
    this.isLoading = false;
  },
  methods: {
    getStepValue(val) {
      this.termChosen = val;
      this.$emit('get-repayment-term', val);
    },
    getDate(dayOffset) {
      const future = new Date();
      future.setDate(future.getDate() + dayOffset);
      return future;
    },
    updateAndContinue() {
      if (!this.selectedTermRecord) {
        return;
      }
      this.$emit('repayment-details', {
        termRate: this.selectedTermRate,
        selectedTerm: this.selectedTerm,
        numWeeklyPayments: this.numWeeklyPayments,
        paymentPerTerm: this.weeklyPayment,
        feeCents: this.selectedTermFeeCents,
        feeVersionId: this.selectedTermFeeVersionId,
        repaymentSchedule: this.selectedTermSchedule,
        dueDate: this.selectedDueDate
          ? this.formatDate(this.selectedDueDate)
          : this.formatDate(this.getDate(1)),
      });
      analytics.track(SEGMENT_EVENTS.if_clicked_bnpl_payment_schedule_next, {
        businessId: this.businessId,
        businessName: this.business.name,
        selectedTerm: this.numWeeklyPayments,
        billAmount: Number(this.amount) || null,
      });
      this.$emit('next-tab');
    },
    updateAmountDebounced(event) {
      if (this.timeoutReference) {
        clearTimeout(this.timeoutReference);
        this.timeoutReference = null;
      }

      this.timeoutReference = setTimeout(() => {
        this.updateAmount(event);
        if (event.value < centsToDollars(this.minInvoiceAmountCents)) {
          analytics.track(SEGMENT_EVENTS.invoice_amount_error_displayed);
        }
      }, 1000);
    },
    updateAmount({ value }) {
      if (!value) return '';
      let newValue = value;
      if (Number.isInteger(value)) {
        newValue = value.toFixed(2);
      }
      this.checkTriggeredAmountError(value);
      return this.$emit('amount', String(newValue));
    },
    checkTriggeredAmountError(value) {
      const isOverMaxFlow =
        this.isCashAdvanceFlow &&
        value > centsToDollars(this.maxCashAdvanceAmountCents);
      if (isOverMaxFlow && !this.userTriggeredAmountError) {
        const analyticsProperties = {
          type: this.isCashAdvanceFlow
            ? BillType.CASH_ADVANCE
            : BillType.INVOICE,
          amount: value,
          error: this.$t(
            'account.pages.pageVendors.bnplPaymentSchedule.capacityError',
            { money: this.$filters.currency(value * 100) },
          ),
        };
        analytics.track(
          SEGMENT_EVENTS.fe_extension_plan_selection_error_triggered,
          analyticsProperties,
        );
        this.userTriggeredAmountError = true;
      }
    },
    disableNext(val) {
      this.disabled = val;
    },
    currencyChange({ value }) {
      this.getFeesSchedule(value);
    },
    updateDueDate(value) {
      this.selectedDueDate = value;
    },
    formatDate(_date) {
      if (typeof _date === 'string') {
        return _date;
      }
      const date = _date instanceof Date ? _date : new Date(_date);

      const year = date.getFullYear();
      const month = `0${date.getMonth() + 1}`.slice(-2);
      const day = `0${date.getDate()}`.slice(-2);

      return `${year}-${month}-${day}`;
    },
    async getFeesSchedule(fromCurrency) {
      await this.$store.dispatchApiAction('GET_FEES_SCHEDULE', {
        dueDate: this.selectedDueDate,
        amountCents: this.amountCents,
        fromCurrency,
        toCurrency: DEFAULT_CURRENCY,
      });
      this.moneyMask.prefix =
        PAY_OUT_CURRENCY_OPTIONS[fromCurrency].symbol_native;
      this.moneyMask.code = PAY_OUT_CURRENCY_OPTIONS[fromCurrency].code;
      this.hasCurrencyChange = fromCurrency !== DEFAULT_CURRENCY;
      this.$emit('pay-out-currency', fromCurrency);
    },
  },
};
</script>

<style lang="less" module>
.form {
  text-align: left;
  width: 100%;

  .form__controls {
    text-align: center;
    justify-content: center;
  }
}

.grey-container {
  min-height: 390px;
  background-color: #f8f8fa;
  margin: 32px 14%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: auto;

  p {
    font-family: @regular-font;
    color: @color-grey-400;
    font-style: normal;
    font-weight: 300;
    font-size: 16px;
    margin: 16px;
    line-height: 24px;
    text-align: center;
  }

  @media only screen and (max-width: 800px) {
    margin: 16px;
  }
}

.amount-wrapper {
  margin: 0 auto;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 8px;
}

.bnpl-subject-to-approval {
  font-size: 12px;
  font-family: @regular-font;
  line-height: 16px;
  font-weight: 300;
  color: #767676;
  margin-top: 30px;
  margin-bottom: 0;
  max-width: 690px;
}

.tooltip {
  color: @color-black;
  padding: 4px;
  cursor: help;
}
</style>
